import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Newspaper, Podcast, FolderKanban, TrendingUp } from 'lucide-react';

interface Stats {
  news: number;
  episodes: number;
  projects: number;
}

export default function DashboardOverview() {
  const [stats, setStats] = useState<Stats>({ news: 0, episodes: 0, projects: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [newsRes, episodesRes, projectsRes] = await Promise.all([
        supabase.from('news_posts').select('id', { count: 'exact', head: true }),
        supabase.from('episodes').select('id', { count: 'exact', head: true }),
        supabase.from('projects').select('id', { count: 'exact', head: true }),
      ]);

      setStats({
        news: newsRes.count || 0,
        episodes: episodesRes.count || 0,
        projects: projectsRes.count || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'News Articles', value: stats.news, icon: Newspaper, color: 'red' },
    { label: 'Episodes', value: stats.episodes, icon: Podcast, color: 'yellow' },
    { label: 'Projects', value: stats.projects, icon: FolderKanban, color: 'purple' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-yellow-400 font-horizon text-2xl animate-pulse">
          Loading Dashboard...
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-horizon text-4xl text-yellow-400 mb-2">Dashboard Overview</h1>
        <p className="text-gray-400">Welcome to the WFP Studio admin portal</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className={`bg-gradient-to-br from-${card.color}-900/40 to-black border-2 border-${card.color}-600/50 rounded-xl p-6 hover:scale-105 transition-transform`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`bg-${card.color}-500/20 p-3 rounded-lg`}>
                  <Icon className={`text-${card.color}-400`} size={24} />
                </div>
                <TrendingUp className="text-green-400" size={20} />
              </div>
              <h3 className="text-gray-400 text-sm font-bold mb-1">{card.label}</h3>
              <p className="text-white text-3xl font-bold">{card.value}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-gradient-to-br from-red-900/20 to-black border border-yellow-400/30 rounded-xl p-6">
        <h2 className="font-horizon text-2xl text-yellow-400 mb-4">Quick Actions</h2>
        <p className="text-gray-300 mb-4">
          Use the sidebar navigation to manage your content. You can create, edit, and delete
          news articles, episodes, projects, and media files.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-black/50 rounded-lg p-4 border border-gray-700">
            <h3 className="text-yellow-400 font-bold mb-2">News & Blog</h3>
            <p className="text-gray-400 text-sm">
              Create and manage blog posts and news articles. Your author profile will be
              automatically attached to posts you create.
            </p>
          </div>
          <div className="bg-black/50 rounded-lg p-4 border border-gray-700">
            <h3 className="text-yellow-400 font-bold mb-2">Episodes</h3>
            <p className="text-gray-400 text-sm">
              Manage podcast episodes, upload media files, and organize episode content.
            </p>
          </div>
          <div className="bg-black/50 rounded-lg p-4 border border-gray-700">
            <h3 className="text-yellow-400 font-bold mb-2">Projects</h3>
            <p className="text-gray-400 text-sm">
              Showcase WFP Studio projects and manage project information.
            </p>
          </div>
          <div className="bg-black/50 rounded-lg p-4 border border-gray-700">
            <h3 className="text-yellow-400 font-bold mb-2">Media Library</h3>
            <p className="text-gray-400 text-sm">
              Upload and manage images and other media files for your content.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
